import { useState } from 'react';
import type { CharacterDetail } from '@/mocks/characterDetail';
import type { NomoCharacter } from '@/mocks/characters';

interface GalleryProps {
  detail?: CharacterDetail;
  character: NomoCharacter;
}

export default function Gallery({ detail, character }: GalleryProps) {
  const items = detail?.gallery ?? [];
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const close = () => setActiveIndex(null);

  return (
    <section className="bg-background-100 py-16 md:py-28">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="reveal mb-12 md:mb-16">
          <div className="mb-6 flex items-center gap-3">
            <span className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
              Gallery
            </span>
            <span className="h-px w-8 bg-primary-300"></span>
            <span className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
              图库
            </span>
          </div>
          <h2 className="font-heading text-3xl tracking-[0.06em] text-foreground-950 md:text-4xl">
            看看它
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, index) => (
            <button
              key={item.caption}
              type="button"
              onClick={() => setActiveIndex(index)}
              className="reveal group block overflow-hidden rounded-lg border border-background-200/70 bg-background-50 text-left cursor-pointer"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="relative aspect-square w-full overflow-hidden bg-background-100">
                <img
                  src={item.image}
                  alt={`${item.caption} NOMOISM ${character.name}潮玩图库`}
                  title={`NOMOISM ${character.name} · ${item.caption}`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
                />
              </div>
              <div className="px-4 py-3">
                <p className="text-sm text-foreground-700">{item.caption}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {activeIndex !== null && items[activeIndex] && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/80 p-4 backdrop-blur-sm"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="图片预览"
        >
          <div
            className="relative max-w-2xl w-full"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              aria-label="关闭预览"
              className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-background-50 text-foreground-900 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <img
              src={items[activeIndex].image}
              alt={`${items[activeIndex].caption} NOMOISM ${character.name}潮玩图库`}
              title={`NOMOISM ${character.name} · ${items[activeIndex].caption}`}
              className="w-full rounded-lg"
            />
            <p className="mt-3 text-center text-sm text-background-50">
              {items[activeIndex].caption}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}